import dayjs, { Dayjs } from 'dayjs';

import { Geolocation } from '../../types/Location';
import {
  CreateOpenClassSessionParams,
  OpenClassSession,
  OpenClassStatus,
  UpdateOpenClassSessionParams,
} from '../types/OpenClass';

export type SessionDraft = {
  location: string;
  note: string;
  startAt: Dayjs | null;
  endAt: Dayjs | null;
  capacity: number;
  instructorIds: number[];
  confirmed: boolean;
  canceled: boolean;
  geolocation: Geolocation | null;
  placeId: string | null;
};

export const emptySessionDraft = (capacity = 20): SessionDraft => ({
  location: '',
  note: '',
  startAt: null,
  endAt: null,
  capacity,
  instructorIds: [],
  confirmed: false,
  canceled: false,
  geolocation: null,
  placeId: null,
});

export const resizeSessionDrafts = (
  current: SessionDraft[],
  count: number,
): SessionDraft[] => {
  if (count <= current.length) {
    return current.slice(0, count);
  }

  const inheritedCapacity = current[current.length - 1]?.capacity ?? 20;
  return [
    ...current,
    ...Array.from({ length: count - current.length }, () =>
      emptySessionDraft(inheritedCapacity),
    ),
  ];
};

export const isSessionDraftValid = (draft: SessionDraft): boolean =>
  draft.location.trim().length > 0 &&
  !!draft.startAt &&
  !!draft.endAt &&
  draft.endAt.isAfter(draft.startAt) &&
  draft.capacity > 0;

// Confirming a session with no instructor is a 409, so a draft that lost its instructors doesn't
// send the flag.
export const isSessionDraftConfirmable = (draft: SessionDraft): boolean =>
  draft.instructorIds.length > 0;

export const draftFromSession = (session: OpenClassSession): SessionDraft => ({
  location: session.location ?? '',
  note: session.comment ?? '',
  startAt: dayjs(session.startAt),
  endAt: dayjs(session.endAt),
  capacity: session.capacity,
  instructorIds: session.instructors
    .map(({ id }) => id)
    .filter((id): id is number => id != null),
  confirmed: session.status === OpenClassStatus.Confirmed,
  canceled: session.status === OpenClassStatus.Canceled,
  geolocation:
    session.lat != null && session.lng != null
      ? { lat: session.lat, lng: session.lng }
      : null,
  placeId: null,
});

export const sessionDraftToCreateParams = (
  draft: SessionDraft,
): CreateOpenClassSessionParams => ({
  startAt: draft.startAt!.toISOString(),
  endAt: draft.endAt!.toISOString(),
  capacity: draft.capacity,
  instructorIds: draft.instructorIds,
  location: draft.location.trim(),
  lat: draft.geolocation?.lat ?? null,
  lng: draft.geolocation?.lng ?? null,
  comment: draft.note.trim() || null,
  confirmed: draft.confirmed && isSessionDraftConfirmable(draft),
});

// An absent flag means leave that timestamp alone, so only a flag the booker flipped is sent.
export const sessionDraftToUpdateParams = (
  draft: SessionDraft,
  original: SessionDraft,
): UpdateOpenClassSessionParams => {
  const { confirmed, ...fields } = sessionDraftToCreateParams(draft);
  return {
    ...fields,
    ...(confirmed !== original.confirmed ? { confirmed } : {}),
    ...(draft.canceled !== original.canceled
      ? { canceled: draft.canceled }
      : {}),
  };
};
